/* eslint-disable camelcase */
import { TranslateFn } from '@blocs.i18n';

export interface TranslatedObject {
  [key: string]: string | TranslatedObject;
}

// This is the full set of translation keys used by the Cloudiary Widget
// copied from here 2022-01-11 https://upload-widget.cloudinary.com/2.0.145/global/text.json
// INTL Doc: https://cloudinary.com/documentation/upload_widget#localization
export const buildCloudinaryLocalization = (languageCode: string, tr: TranslateFn): TranslatedObject => {
  // cloudinary uses its own variables in some strings, so we don't want to
  // try and do interpolation on them
  const t = (key: string) =>
    tr(key, {
      interpolation: {
        skipOnVariables: true,
      },
    });

  return {
    [languageCode]: {
      or: t('common:cloudinaryUploader.or'),
      back: t('common:cloudinaryUploader.back'),
      advanced: t('common:cloudinaryUploader.advanced'),
      close: t('common:cloudinaryUploader.close'),
      no_results: t('common:cloudinaryUploader.no_results'),
      search_placeholder: t('common:cloudinaryUploader.search_placeholder'),
      about_uw: t('common:cloudinaryUploader.about_uw'),
      search: {
        placeholder: t('common:cloudinaryUploader.search.placeholder'),
        reset: t('common:cloudinaryUploader.search.reset'),
      },
      menu: {
        files: t('common:cloudinaryUploader.menu.files'),
        web: t('common:cloudinaryUploader.menu.web'),
        camera: t('common:cloudinaryUploader.menu.camera'),
        gsearch: t('common:cloudinaryUploader.menu.gsearch'),
        gdrive: t('common:cloudinaryUploader.menu.gdrive'),
        dropbox: t('common:cloudinaryUploader.menu.dropbox'),
        facebook: t('common:cloudinaryUploader.menu.facebook'),
        instagram: t('common:cloudinaryUploader.menu.instagram'),
        shutterstock: t('common:cloudinaryUploader.menu.shutterstock'),
        istock: t('common:cloudinaryUploader.menu.istock'),
        unsplash: t('common:cloudinaryUploader.menu.unsplash'),
      },
      selection_counter: {
        selected: t('common:cloudinaryUploader.selection_counter.selected'),
      },
      actions: {
        upload: t('common:cloudinaryUploader.actions.upload'),
        next: t('common:cloudinaryUploader.actions.next'),
        clear_all: t('common:cloudinaryUploader.actions.clear_all'),
        log_out: t('common:cloudinaryUploader.actions.log_out'),
      },
      notifications: {
        general_error: t('common:cloudinaryUploader.notifications.general_error'),
        general_prompt: t('common:cloudinaryUploader.notifications.general_prompt'),
        limit_reached: t('common:cloudinaryUploader.notifications.limit_reached'),
        invalid_add_url: t('common:cloudinaryUploader.notifications.invalid_add_url'),
        invalid_public_id: t('common:cloudinaryUploader.notifications.invalid_public_id'),
        no_new_files: t('common:cloudinaryUploader.notifications.no_new_files'),
        image_purchased: t('common:cloudinaryUploader.notifications.image_purchased'),
        video_purchased: t('common:cloudinaryUploader.notifications.video_purchased'),
        purchase_failed: t('common:cloudinaryUploader.notifications.purchase_failed'),
        service_logged_out: t('common:cloudinaryUploader.notifications.service_logged_out'),
        great: t('common:cloudinaryUploader.notifications.great'),
      },
      advanced_options: {
        public_id_ph: t('common:cloudinaryUploader.advanced_options.public_id_ph'),
        tags_ph: t('common:cloudinaryUploader.advanced_options.tags_ph'),
        add_new: t('common:cloudinaryUploader.advanced_options.add_new'),
        upload_preset_placeholder: t('common:cloudinaryUploader.advanced_options.upload_preset_placeholder'),
      },
      landscape_overlay: {
        title: t('common:cloudinaryUploader.landscape_overlay.title'),
        description: t('common:cloudinaryUploader.landscape_overlay.description'),
      },
      aria_label: {
        close: t('common:cloudinaryUploader.aria_label.close'),
        back: t('common:cloudinaryUploader.aria_label.back'),
      },
      queue: {
        title: t('common:cloudinaryUploader.queue.title'),
        title_uploading_with_counter: t('common:cloudinaryUploader.queue.title_uploading_with_counter'),
        title_uploading: t('common:cloudinaryUploader.queue.title_uploading'),
        mini_title: t('common:cloudinaryUploader.queue.mini_title'),
        mini_title_uploading: t('common:cloudinaryUploader.queue.mini_title_uploading'),
        show_completed: t('common:cloudinaryUploader.queue.show_completed'),
        retry_failed: t('common:cloudinaryUploader.queue.retry_failed'),
        abort_all: t('common:cloudinaryUploader.queue.abort_all'),
        upload_more: t('common:cloudinaryUploader.queue.upload_more'),
        done: t('common:cloudinaryUploader.queue.done'),
        mini_upload_count: t('common:cloudinaryUploader.queue.mini_upload_count'),
        mini_failed: t('common:cloudinaryUploader.queue.mini_failed'),
        statuses: {
          uploading: t('common:cloudinaryUploader.queue.statuses.uploading'),
          error: t('common:cloudinaryUploader.queue.statuses.error'),
          uploaded: t('common:cloudinaryUploader.queue.statuses.uploaded'),
          aborted: t('common:cloudinaryUploader.queue.statuses.aborted'),
        },
      },
      uploader: {
        filesize: {
          na: t('common:cloudinaryUploader.uploader.filesize.na'),
          b: t('common:cloudinaryUploader.uploader.filesize.b'),
          k: t('common:cloudinaryUploader.uploader.filesize.k'),
          m: t('common:cloudinaryUploader.uploader.filesize.m'),
          g: t('common:cloudinaryUploader.uploader.filesize.g'),
          t: t('common:cloudinaryUploader.uploader.filesize.t'),
        },
        errors: {
          file_too_large: t('common:cloudinaryUploader.uploader.errors.file_too_large'),
          max_dimensions_validation: t('common:cloudinaryUploader.uploader.errors.max_dimensions_validation'),
          min_dimensions_validation: t('common:cloudinaryUploader.uploader.errors.min_dimensions_validation'),
          unavailable: t('common:cloudinaryUploader.uploader.errors.unavailable'),
          max_number_of_files: t('common:cloudinaryUploader.uploader.errors.max_number_of_files'),
          allowed_formats: t('common:cloudinaryUploader.uploader.errors.allowed_formats'),
          max_file_size: t('common:cloudinaryUploader.uploader.errors.max_file_size'),
          min_file_size: t('common:cloudinaryUploader.uploader.errors.min_file_size'),
        },
        close_mid_upload: t('common:cloudinaryUploader.uploader.close_mid_upload'),
      },
      crop: {
        title: t('common:cloudinaryUploader.crop.title'),
        crop_btn: t('common:cloudinaryUploader.crop.crop_btn'),
        skip_btn: t('common:cloudinaryUploader.crop.skip_btn'),
        reset_btn: t('common:cloudinaryUploader.crop.reset_btn'),
        close_btn: t('common:cloudinaryUploader.crop.close_btn'),
        close_prompt: t('common:cloudinaryUploader.crop.close_prompt'),
        image_error: t('common:cloudinaryUploader.crop.image_error'),
        corner_tooltip: t('common:cloudinaryUploader.crop.corner_tooltip'),
        handle_tooltip: t('common:cloudinaryUploader.crop.handle_tooltip'),
      },
      camera: {
        capture: t('common:cloudinaryUploader.camera.capture'),
        cancel: t('common:cloudinaryUploader.camera.cancel'),
        take_pic: t('common:cloudinaryUploader.camera.take_pic'),
        explanation: t('common:cloudinaryUploader.camera.explanation'),
        camera_error: t('common:cloudinaryUploader.camera.camera_error'),
        retry: t('common:cloudinaryUploader.camera.retry'),
        file_name: t('common:cloudinaryUploader.camera.file_name'),
      },
      dropbox: {
        no_auth_title: t('common:cloudinaryUploader.dropbox.no_auth_title'),
        no_auth_action: t('common:cloudinaryUploader.dropbox.no_auth_action'),
        no_photos: t('common:cloudinaryUploader.dropbox.no_photos'),
        no_files: t('common:cloudinaryUploader.dropbox.no_files'),
        root_crumb: t('common:cloudinaryUploader.dropbox.root_crumb'),
        list_headers: {
          select: t('common:cloudinaryUploader.dropbox.list_headers.select'),
          name: t('common:cloudinaryUploader.dropbox.list_headers.name'),
          modified: t('common:cloudinaryUploader.dropbox.list_headers.modified'),
        },
        menu: {
          browse: t('common:cloudinaryUploader.dropbox.menu.browse'),
          recent: t('common:cloudinaryUploader.dropbox.menu.recent'),
        },
        authenticating: t('common:cloudinaryUploader.dropbox.authenticating'),
      },
      facebook: {
        no_photos: t('common:cloudinaryUploader.facebook.no_photos'),
        no_auth_title: t('common:cloudinaryUploader.facebook.no_auth_title'),
        no_auth_action: t('common:cloudinaryUploader.facebook.no_auth_action'),
        no_auth_statement: t('common:cloudinaryUploader.facebook.no_auth_statement'),
        album_subtitle: t('common:cloudinaryUploader.facebook.album_subtitle'),
        menu: {
          uploaded: t('common:cloudinaryUploader.facebook.menu.uploaded'),
          tagged: t('common:cloudinaryUploader.facebook.menu.tagged'),
          albums: t('common:cloudinaryUploader.facebook.menu.albums'),
        },
      },
      google_drive: {
        no_auth_title: t('common:cloudinaryUploader.google_drive.no_auth_title'),
        no_auth_action: t('common:cloudinaryUploader.google_drive.no_auth_action'),
        search: {
          placeholder: t('common:cloudinaryUploader.google_drive.search.placeholder'),
          reset: t('common:cloudinaryUploader.google_drive.search.reset'),
        },
        grid: {
          folders: t('common:cloudinaryUploader.google_drive.grid.folders'),
          files: t('common:cloudinaryUploader.google_drive.grid.files'),
          empty_folder: t('common:cloudinaryUploader.google_drive.grid.empty_folder'),
        },
        recent: t('common:cloudinaryUploader.google_drive.recent'),
        starred: t('common:cloudinaryUploader.google_drive.starred'),
        my_drive: t('common:cloudinaryUploader.google_drive.my_drive'),
        shared_drive: t('common:cloudinaryUploader.google_drive.shared_drive'),
        search_results: t('common:cloudinaryUploader.google_drive.search_results'),
        shared_with_me: t('common:cloudinaryUploader.google_drive.shared_with_me'),
        name: t('common:cloudinaryUploader.google_drive.name'),
        modifiedTime: t('common:cloudinaryUploader.google_drive.modifiedTime'),
        modifiedByMeTime: t('common:cloudinaryUploader.google_drive.modifiedByMeTime'),
        viewedByMeTime: t('common:cloudinaryUploader.google_drive.viewedByMeTime'),
        notifications: {
          retrieve_failed: t('common:cloudinaryUploader.google_drive.notifications.retrieve_failed'),
        },
      },
      image_search: {
        main_title: t('common:cloudinaryUploader.image_search.main_title'),
        inputPlaceholder: t('common:cloudinaryUploader.image_search.inputPlaceholder'),
        customPlaceholder: t('common:cloudinaryUploader.image_search.customPlaceholder'),
        show_options: t('common:cloudinaryUploader.image_search.show_options'),
        hide_options: t('common:cloudinaryUploader.image_search.hide_options'),
        filters_title: t('common:cloudinaryUploader.image_search.filters_title'),
        all: t('common:cloudinaryUploader.image_search.all'),
        rights: t('common:cloudinaryUploader.image_search.rights'),
        rights_options: {
          not_filtered: t('common:cloudinaryUploader.image_search.rights_options.not_filtered'),
          free: t('common:cloudinaryUploader.image_search.rights_options.free'),
          free_com: t('common:cloudinaryUploader.image_search.rights_options.free_com'),
          free_mod: t('common:cloudinaryUploader.image_search.rights_options.free_mod'),
          free_mod_com: t('common:cloudinaryUploader.image_search.rights_options.free_mod_com'),
        },
        search_error: t('common:cloudinaryUploader.image_search.search_error'),
      },
      instagram: {
        no_auth_title: t('common:cloudinaryUploader.instagram.no_auth_title'),
        no_auth_action: t('common:cloudinaryUploader.instagram.no_auth_action'),
        header_title: t('common:cloudinaryUploader.instagram.header_title'),
        authenticating: t('common:cloudinaryUploader.instagram.authenticating'),
      },
      local: {
        browse: t('common:cloudinaryUploader.local.browse'),
        dd_title_single: t('common:cloudinaryUploader.local.dd_title_single'),
        dd_title_multi: t('common:cloudinaryUploader.local.dd_title_multi'),
        drop_title_single: t('common:cloudinaryUploader.local.drop_title_single'),
        drop_title_multiple: t('common:cloudinaryUploader.local.drop_title_multiple'),
      },
      shutterstock: {
        no_auth_title: t('common:cloudinaryUploader.shutterstock.no_auth_title'),
        toggle_filters_button: t('common:cloudinaryUploader.shutterstock.toggle_filters_button'),
        no_auth_action: t('common:cloudinaryUploader.shutterstock.no_auth_action'),
        authenticating: t('common:cloudinaryUploader.shutterstock.authenticating'),
        statement: t('common:cloudinaryUploader.shutterstock.statement'),
        reg_link_text: t('common:cloudinaryUploader.shutterstock.reg_link_text'),
        next_btn: t('common:cloudinaryUploader.shutterstock.next_btn'),
        media_types: {
          images: t('common:cloudinaryUploader.shutterstock.media_types.images'),
          videos: t('common:cloudinaryUploader.shutterstock.media_types.videos'),
        },
        filters: {
          more_label: t('common:cloudinaryUploader.shutterstock.filters.more_label'),
          sort_options: {
            label: t('common:cloudinaryUploader.shutterstock.filters.sort_options.label'),
            newest: t('common:cloudinaryUploader.shutterstock.filters.sort_options.newest'),
            relevance: t('common:cloudinaryUploader.shutterstock.filters.sort_options.relevance'),
            popular: t('common:cloudinaryUploader.shutterstock.filters.sort_options.popular'),
            random: t('common:cloudinaryUploader.shutterstock.filters.sort_options.random'),
          },
          people: {
            label: t('common:cloudinaryUploader.shutterstock.filters.people.label'),
            only_images_with_people: t('common:cloudinaryUploader.shutterstock.filters.people.only_images_with_people'),
            only_videos_with_people: t('common:cloudinaryUploader.shutterstock.filters.people.only_videos_with_people'),
          },
          gender: {
            label: t('common:cloudinaryUploader.shutterstock.filters.gender.label'),
            male: t('common:cloudinaryUploader.shutterstock.filters.gender.male'),
            female: t('common:cloudinaryUploader.shutterstock.filters.gender.female'),
            both: t('common:cloudinaryUploader.shutterstock.filters.gender.both'),
          },
          size: {
            label: t('common:cloudinaryUploader.shutterstock.filters.size.label'),
            small: t('common:cloudinaryUploader.shutterstock.filters.size.small'),
            medium: t('common:cloudinaryUploader.shutterstock.filters.size.medium'),
            large: t('common:cloudinaryUploader.shutterstock.filters.size.large'),
          },
          clear: t('common:cloudinaryUploader.shutterstock.filters.clear'),
          orientation: {
            label: t('common:cloudinaryUploader.shutterstock.filters.orientation.label'),
            horizontal: t('common:cloudinaryUploader.shutterstock.filters.orientation.horizontal'),
            vertical: t('common:cloudinaryUploader.shutterstock.filters.orientation.vertical'),
          },
          color: t('common:cloudinaryUploader.shutterstock.filters.color'),
          colors: {
            red: t('common:cloudinaryUploader.shutterstock.filters.colors.red'),
            orange: t('common:cloudinaryUploader.shutterstock.filters.colors.orange'),
            amber: t('common:cloudinaryUploader.shutterstock.filters.colors.amber'),
            yellow: t('common:cloudinaryUploader.shutterstock.filters.colors.yellow'),
            lime: t('common:cloudinaryUploader.shutterstock.filters.colors.lime'),
            green: t('common:cloudinaryUploader.shutterstock.filters.colors.green'),
            teal: t('common:cloudinaryUploader.shutterstock.filters.colors.teal'),
            turquoise: t('common:cloudinaryUploader.shutterstock.filters.colors.turquoise'),
            aqua: t('common:cloudinaryUploader.shutterstock.filters.colors.aqua'),
            azure: t('common:cloudinaryUploader.shutterstock.filters.colors.azure'),
            blue: t('common:cloudinaryUploader.shutterstock.filters.colors.blue'),
            purple: t('common:cloudinaryUploader.shutterstock.filters.colors.purple'),
            orchid: t('common:cloudinaryUploader.shutterstock.filters.colors.orchid'),
            magenta: t('common:cloudinaryUploader.shutterstock.filters.colors.magenta'),
          },
          safe: t('common:cloudinaryUploader.shutterstock.filters.safe'),
          all_categories: t('common:cloudinaryUploader.shutterstock.filters.all_categories'),
          types: {
            all: t('common:cloudinaryUploader.shutterstock.filters.types.all'),
            illustration: t('common:cloudinaryUploader.shutterstock.filters.types.illustration'),
            photo: t('common:cloudinaryUploader.shutterstock.filters.types.photo'),
            vector: t('common:cloudinaryUploader.shutterstock.filters.types.vector'),
          },
          duration: {
            label: t('common:cloudinaryUploader.shutterstock.filters.duration.label'),
            short: t('common:cloudinaryUploader.shutterstock.filters.duration.short'),
            short_tip: t('common:cloudinaryUploader.shutterstock.filters.duration.short_tip'),
            long: t('common:cloudinaryUploader.shutterstock.filters.duration.long'),
            long_tip: t('common:cloudinaryUploader.shutterstock.filters.duration.long_tip'),
          },
          resolution: {
            label: t('common:cloudinaryUploader.shutterstock.filters.resolution.label'),
            standard_definition: t('common:cloudinaryUploader.shutterstock.filters.resolution.standard_definition'),
            standard_definition_tip: t(
              'common:cloudinaryUploader.shutterstock.filters.resolution.standard_definition_tip'
            ),
            high_definition: t('common:cloudinaryUploader.shutterstock.filters.resolution.high_definition'),
            high_definition_tip: t('common:cloudinaryUploader.shutterstock.filters.resolution.high_definition_tip'),
            '4k': t('common:cloudinaryUploader.shutterstock.filters.resolution.4k'),
            '4k_tip': t('common:cloudinaryUploader.shutterstock.filters.resolution.4k_tip'),
          },
        },
        filter_summary: {
          label: t('common:cloudinaryUploader.shutterstock.filter_summary.label'),
          gender: t('common:cloudinaryUploader.shutterstock.filter_summary.gender'),
          people: t('common:cloudinaryUploader.shutterstock.filter_summary.people'),
          color: t('common:cloudinaryUploader.shutterstock.filter_summary.color'),
          category: t('common:cloudinaryUploader.shutterstock.filter_summary.category'),
          unsafe: t('common:cloudinaryUploader.shutterstock.filter_summary.unsafe'),
        },
        purchase_page: {
          purchase_btn_label: t('common:cloudinaryUploader.shutterstock.purchase_page.purchase_btn_label'),
          plans: {
            label: t('common:cloudinaryUploader.shutterstock.purchase_page.plans.label'),
            years: t('common:cloudinaryUploader.shutterstock.purchase_page.plans.years'),
            days: t('common:cloudinaryUploader.shutterstock.purchase_page.plans.days'),
            expired: t('common:cloudinaryUploader.shutterstock.purchase_page.plans.expired'),
            exceeded: t('common:cloudinaryUploader.shutterstock.purchase_page.plans.exceeded'),
            manage_plans: t('common:cloudinaryUploader.shutterstock.purchase_page.plans.manage_plans'),
            no_plan: t('common:cloudinaryUploader.shutterstock.purchase_page.plans.no_plan'),
          },
          format_and_size: {
            label: t('common:cloudinaryUploader.shutterstock.purchase_page.format_and_size.label'),
          },
          aspect_ratio: t('common:cloudinaryUploader.shutterstock.purchase_page.aspect_ratio'),
          author: t('common:cloudinaryUploader.shutterstock.purchase_page.author'),
          description: t('common:cloudinaryUploader.shutterstock.purchase_page.description'),
          button_label: {
            purchase: t('common:cloudinaryUploader.shutterstock.purchase_page.button_label.purchase'),
            acquire: t('common:cloudinaryUploader.shutterstock.purchase_page.button_label.acquire'),
            next: t('common:cloudinaryUploader.shutterstock.purchase_page.button_label.next'),
            upload: t('common:cloudinaryUploader.shutterstock.purchase_page.button_label.upload'),
            purchasing: t('common:cloudinaryUploader.shutterstock.purchase_page.button_label.purchasing'),
            downloading: t('common:cloudinaryUploader.shutterstock.purchase_page.button_label.downloading'),
          },
          button_description: {
            next: t('common:cloudinaryUploader.shutterstock.purchase_page.button_description.next'),
            upload: t('common:cloudinaryUploader.shutterstock.purchase_page.button_description.upload'),
            purchasing: t('common:cloudinaryUploader.shutterstock.purchase_page.button_description.purchasing'),
            downloading: t('common:cloudinaryUploader.shutterstock.purchase_page.button_description.downloading'),
          },
          duration: t('common:cloudinaryUploader.shutterstock.purchase_page.duration'),
          fps: t('common:cloudinaryUploader.shutterstock.purchase_page.fps'),
          sizes: {
            vector_eps: t('common:cloudinaryUploader.shutterstock.purchase_page.sizes.vector_eps'),
            small_jpg: t('common:cloudinaryUploader.shutterstock.purchase_page.sizes.small_jpg'),
            medium_jpg: t('common:cloudinaryUploader.shutterstock.purchase_page.sizes.medium_jpg'),
            huge_jpg: t('common:cloudinaryUploader.shutterstock.purchase_page.sizes.huge_jpg'),
            web: t('common:cloudinaryUploader.shutterstock.purchase_page.sizes.web'),
            sd: t('common:cloudinaryUploader.shutterstock.purchase_page.sizes.sd'),
            hd: t('common:cloudinaryUploader.shutterstock.purchase_page.sizes.hd'),
            '4k': t('common:cloudinaryUploader.shutterstock.purchase_page.sizes.4k'),
          },
          file_limit_exceeded: t('common:cloudinaryUploader.shutterstock.purchase_page.file_limit_exceeded'),
          time_left: t('common:cloudinaryUploader.shutterstock.purchase_page.time_left'),
        },
        purchase_preview: {
          loading_preview: t('common:cloudinaryUploader.shutterstock.purchase_preview.loading_preview'),
          open_in_new_window: t('common:cloudinaryUploader.shutterstock.purchase_preview.open_in_new_window'),
        },
        dictionary: {
          image: t('common:cloudinaryUploader.shutterstock.dictionary.image'),
          video: t('common:cloudinaryUploader.shutterstock.dictionary.video'),
        },
        home_page: {
          results_header: t('common:cloudinaryUploader.shutterstock.home_page.results_header'),
          categories_header: t('common:cloudinaryUploader.shutterstock.home_page.categories_header'),
        },
        search: {
          placeholder: t('common:cloudinaryUploader.shutterstock.search.placeholder'),
          reset: t('common:cloudinaryUploader.shutterstock.search.reset'),
        },
      },
      istock: {
        no_auth_title: t('common:cloudinaryUploader.istock.no_auth_title'),
        no_auth_action: t('common:cloudinaryUploader.istock.no_auth_action'),
        no_auth_statement: t('common:cloudinaryUploader.istock.no_auth_statement'),
        reg_link_text: t('common:cloudinaryUploader.istock.reg_link_text'),
        select: t('common:cloudinaryUploader.istock.select'),
        search: {
          placeholder: t('common:cloudinaryUploader.istock.search.placeholder'),
          reset: t('common:cloudinaryUploader.istock.search.reset'),
        },
        media_types: {
          images: t('common:cloudinaryUploader.istock.media_types.images'),
          videos: t('common:cloudinaryUploader.istock.media_types.videos'),
        },
        media_types_singular: {
          images: t('common:cloudinaryUploader.istock.media_types_singular.images'),
          videos: t('common:cloudinaryUploader.istock.media_types_singular.videos'),
        },
        popular_categories: t('common:cloudinaryUploader.istock.popular_categories'),
        diversity_categories: t('common:cloudinaryUploader.istock.diversity_categories'),
        filters: {
          toggle_filters_button: t('common:cloudinaryUploader.istock.filters.toggle_filters_button'),
          clear: t('common:cloudinaryUploader.istock.filters.clear'),
          sort_options: {
            label: t('common:cloudinaryUploader.istock.filters.sort_options.label'),
            newest: t('common:cloudinaryUploader.istock.filters.sort_options.newest'),
            best_match: t('common:cloudinaryUploader.istock.filters.sort_options.best_match'),
            most_popular: t('common:cloudinaryUploader.istock.filters.sort_options.most_popular'),
            random: t('common:cloudinaryUploader.istock.filters.sort_options.random'),
          },
          orientation: {
            label: t('common:cloudinaryUploader.istock.filters.orientation.label'),
            horizontal: t('common:cloudinaryUploader.istock.filters.orientation.horizontal'),
            vertical: t('common:cloudinaryUploader.istock.filters.orientation.vertical'),
            square: t('common:cloudinaryUploader.istock.filters.orientation.square'),
            panoramic_horizontal: t('common:cloudinaryUploader.istock.filters.orientation.panoramic_horizontal'),
            panoramic_vertical: t('common:cloudinaryUploader.istock.filters.orientation.panoramic_vertical'),
          },
          number_of_people: {
            label: t('common:cloudinaryUploader.istock.filters.number_of_people.label'),
            none: t('common:cloudinaryUploader.istock.filters.number_of_people.none'),
            one: t('common:cloudinaryUploader.istock.filters.number_of_people.one'),
            two: t('common:cloudinaryUploader.istock.filters.number_of_people.two'),
            group: t('common:cloudinaryUploader.istock.filters.number_of_people.group'),
          },
          license: {
            label: t('common:cloudinaryUploader.istock.filters.license.label'),
            creative: t('common:cloudinaryUploader.istock.filters.license.creative'),
            editorial: t('common:cloudinaryUploader.istock.filters.license.editorial'),
          },
          colors: {
            label: t('common:cloudinaryUploader.istock.filters.colors.label'),
          },
          age_of_people: {
            label: t('common:cloudinaryUploader.istock.filters.age_of_people.label'),
            baby: t('common:cloudinaryUploader.istock.filters.age_of_people.baby'),
            child: t('common:cloudinaryUploader.istock.filters.age_of_people.child'),
            teenager: t('common:cloudinaryUploader.istock.filters.age_of_people.teenager'),
            young_adult: t('common:cloudinaryUploader.istock.filters.age_of_people.young_adult'),
            adult: t('common:cloudinaryUploader.istock.filters.age_of_people.adult'),
            adults_only: t('common:cloudinaryUploader.istock.filters.age_of_people.adults_only'),
            mature_adult: t('common:cloudinaryUploader.istock.filters.age_of_people.mature_adult'),
            senior_adult: t('common:cloudinaryUploader.istock.filters.age_of_people.senior_adult'),
          },
          ethnicity: {
            label: t('common:cloudinaryUploader.istock.filters.ethnicity.label'),
            black: t('common:cloudinaryUploader.istock.filters.ethnicity.black'),
            caucasian: t('common:cloudinaryUploader.istock.filters.ethnicity.caucasian'),
            east_asian: t('common:cloudinaryUploader.istock.filters.ethnicity.east_asian'),
            hispanic_latino: t('common:cloudinaryUploader.istock.filters.ethnicity.hispanic_latino'),
            middle_eastern: t('common:cloudinaryUploader.istock.filters.ethnicity.middle_eastern'),
            mixed_race_person: t('common:cloudinaryUploader.istock.filters.ethnicity.mixed_race_person'),
            multiethnic_group: t('common:cloudinaryUploader.istock.filters.ethnicity.multiethnic_group'),
            native_american_first_nations: t(
              'common:cloudinaryUploader.istock.filters.ethnicity.native_american_first_nations'
            ),
            pacific_islander: t('common:cloudinaryUploader.istock.filters.ethnicity.pacific_islander'),
            south_asian: t('common:cloudinaryUploader.istock.filters.ethnicity.south_asian'),
            southeast_asian: t('common:cloudinaryUploader.istock.filters.ethnicity.southeast_asian'),
          },
          resolution: {
            label: t('common:cloudinaryUploader.istock.filters.resolution.label'),
          },
          duration: {
            label: t('common:cloudinaryUploader.istock.filters.duration.label'),
          },
          more: {
            label: t('common:cloudinaryUploader.istock.filters.more.label'),
            safe: t('common:cloudinaryUploader.istock.filters.more.safe'),
          },
          summary: {
            label: t('common:cloudinaryUploader.istock.filters.summary.label'),
            color: t('common:cloudinaryUploader.istock.filters.summary.color'),
            number_of_people: {
              none: t('common:cloudinaryUploader.istock.filters.summary.number_of_people.none'),
              one: t('common:cloudinaryUploader.istock.filters.summary.number_of_people.one'),
              two: t('common:cloudinaryUploader.istock.filters.summary.number_of_people.two'),
              group: t('common:cloudinaryUploader.istock.filters.summary.number_of_people.group'),
            },
          },
        },
        purchase: {
          select_plan: t('common:cloudinaryUploader.istock.purchase.select_plan'),
          select_format: t('common:cloudinaryUploader.istock.purchase.select_format'),
          manage_plans: t('common:cloudinaryUploader.istock.purchase.manage_plans'),
          format: t('common:cloudinaryUploader.istock.purchase.format'),
          credit: t('common:cloudinaryUploader.istock.purchase.credit'),
          credits: t('common:cloudinaryUploader.istock.purchase.credits'),
          istock_photo_id: t('common:cloudinaryUploader.istock.purchase.istock_photo_id'),
          istock_video_id: t('common:cloudinaryUploader.istock.purchase.istock_video_id'),
          plan_exceeded: t('common:cloudinaryUploader.istock.purchase.plan_exceeded'),
          size: {
            small: t('common:cloudinaryUploader.istock.purchase.size.small'),
            medium: t('common:cloudinaryUploader.istock.purchase.size.medium'),
            large: t('common:cloudinaryUploader.istock.purchase.size.large'),
            x_large: t('common:cloudinaryUploader.istock.purchase.size.x_large'),
            xx_large: t('common:cloudinaryUploader.istock.purchase.size.xx_large'),
            vector: t('common:cloudinaryUploader.istock.purchase.size.vector'),
          },
          credits_remaining: t('common:cloudinaryUploader.istock.purchase.credits_remaining'),
          media_type_not_included: t('common:cloudinaryUploader.istock.purchase.media_type_not_included'),
          images: t('common:cloudinaryUploader.istock.purchase.images'),
          videos: t('common:cloudinaryUploader.istock.purchase.videos'),
          days_left: t('common:cloudinaryUploader.istock.purchase.days_left'),
          credits_balance_too_small: t('common:cloudinaryUploader.istock.purchase.credits_balance_too_small'),
          collection: t('common:cloudinaryUploader.istock.purchase.collection'),
          cost: t('common:cloudinaryUploader.istock.purchase.cost'),
        },
        buttons: {
          next: t('common:cloudinaryUploader.istock.buttons.next'),
          upload: t('common:cloudinaryUploader.istock.buttons.upload'),
          purchase: t('common:cloudinaryUploader.istock.buttons.purchase'),
        },
      },
      unsplash: {
        no_auth_title: t('common:cloudinaryUploader.unsplash.no_auth_title'),
        no_auth_action: t('common:cloudinaryUploader.unsplash.no_auth_action'),
        no_auth_statement: t('common:cloudinaryUploader.unsplash.no_auth_statement'),
        select: t('common:cloudinaryUploader.unsplash.select'),
        popular_categories: t('common:cloudinaryUploader.unsplash.popular_categories'),
        editorial: t('common:cloudinaryUploader.unsplash.editorial'),
        filters: {
          toggle_filters_button: t('common:cloudinaryUploader.unsplash.filters.toggle_filters_button'),
          clear: t('common:cloudinaryUploader.unsplash.filters.clear'),
          sort_options: {
            label: t('common:cloudinaryUploader.unsplash.filters.sort_options.label'),
            latest: t('common:cloudinaryUploader.unsplash.filters.sort_options.latest'),
            oldest: t('common:cloudinaryUploader.unsplash.filters.sort_options.oldest'),
            relevant: t('common:cloudinaryUploader.unsplash.filters.sort_options.relevant'),
            popular: t('common:cloudinaryUploader.unsplash.filters.sort_options.popular'),
          },
          orientation: {
            label: t('common:cloudinaryUploader.unsplash.filters.orientation.label'),
            portrait: t('common:cloudinaryUploader.unsplash.filters.orientation.portrait'),
            landscape: t('common:cloudinaryUploader.unsplash.filters.orientation.landscape'),
            squarish: t('common:cloudinaryUploader.unsplash.filters.orientation.squarish'),
          },
          categories: {
            label: t('common:cloudinaryUploader.unsplash.filters.categories.label'),
          },
          license: {
            label: t('common:cloudinaryUploader.unsplash.filters.license.label'),
            creative: t('common:cloudinaryUploader.unsplash.filters.license.creative'),
            editorial: t('common:cloudinaryUploader.unsplash.filters.license.editorial'),
          },
          colors: {
            label: t('common:cloudinaryUploader.unsplash.filters.colors.label'),
          },
          summary: {
            label: t('common:cloudinaryUploader.unsplash.filters.summary.label'),
            color: t('common:cloudinaryUploader.unsplash.filters.summary.color'),
          },
          filters_in: t('common:cloudinaryUploader.unsplash.filters.filters_in'),
          color_types: {
            tones: t('common:cloudinaryUploader.unsplash.filters.color_types.tones'),
            black_and_white: t('common:cloudinaryUploader.unsplash.filters.color_types.black_and_white'),
          },
        },
        summary: {
          description: t('common:cloudinaryUploader.unsplash.summary.description'),
          permission: t('common:cloudinaryUploader.unsplash.summary.permission'),
          free_to_use: t('common:cloudinaryUploader.unsplash.summary.free_to_use'),
          published_at: t('common:cloudinaryUploader.unsplash.summary.published_at'),
          location: t('common:cloudinaryUploader.unsplash.summary.location'),
          credit: t('common:cloudinaryUploader.unsplash.summary.credit'),
          format: t('common:cloudinaryUploader.unsplash.summary.format'),
          size: {
            small: t('common:cloudinaryUploader.unsplash.summary.size.small'),
            medium: t('common:cloudinaryUploader.unsplash.summary.size.medium'),
            large: t('common:cloudinaryUploader.unsplash.summary.size.large'),
            original_size: t('common:cloudinaryUploader.unsplash.summary.size.original_size'),
          },
        },
        colors: {
          orange: t('common:cloudinaryUploader.unsplash.colors.orange'),
          red: t('common:cloudinaryUploader.unsplash.colors.red'),
          yellow: t('common:cloudinaryUploader.unsplash.colors.yellow'),
          green: t('common:cloudinaryUploader.unsplash.colors.green'),
          teal: t('common:cloudinaryUploader.unsplash.colors.teal'),
          blue: t('common:cloudinaryUploader.unsplash.colors.blue'),
          purple: t('common:cloudinaryUploader.unsplash.colors.purple'),
          magenta: t('common:cloudinaryUploader.unsplash.colors.magenta'),
          white: t('common:cloudinaryUploader.unsplash.colors.white'),
          black: t('common:cloudinaryUploader.unsplash.colors.black'),
          black_and_white: t('common:cloudinaryUploader.unsplash.colors.black_and_white'),
        },
      },
      url: {
        inner_title: t('common:cloudinaryUploader.url.inner_title'),
        input_placeholder: t('common:cloudinaryUploader.url.input_placeholder'),
      },
      metadataPage: {
        title: t('common:cloudinaryUploader.metadataPage.title'),
        subtitle: t('common:cloudinaryUploader.metadataPage.subtitle'),
        subtitle_plural: t('common:cloudinaryUploader.metadataPage.subtitle_plural'),
        tooltip: t('common:cloudinaryUploader.metadataPage.tooltip'),
        upload: t('common:cloudinaryUploader.metadataPage.upload'),
        conflict_label: t('common:cloudinaryUploader.metadataPage.conflict_label'),
        intro: {
          openingText: t('common:cloudinaryUploader.metadataPage.intro.openingText'),
          conflictWarning: t('common:cloudinaryUploader.metadataPage.intro.conflictWarning'),
          closingText: t('common:cloudinaryUploader.metadataPage.intro.closingText'),
        },
        closePrompt: t('common:cloudinaryUploader.metadataPage.closePrompt'),
        backPrompt: t('common:cloudinaryUploader.metadataPage.backPrompt'),
        approveCancel: t('common:cloudinaryUploader.metadataPage.approveCancel'),
        cancel: t('common:cloudinaryUploader.metadataPage.cancel'),
        validationErrors: {
          string: {
            min: t('common:cloudinaryUploader.metadataPage.validationErrors.string.min'),
            max: t('common:cloudinaryUploader.metadataPage.validationErrors.string.max'),
            minAndMax: t('common:cloudinaryUploader.metadataPage.validationErrors.string.minAndMax'),
            regex: t('common:cloudinaryUploader.metadataPage.validationErrors.string.regex'),
          },
          integer: {
            lessThan: t('common:cloudinaryUploader.metadataPage.validationErrors.integer.lessThan'),
            lessThanEqual: t('common:cloudinaryUploader.metadataPage.validationErrors.integer.lessThanEqual'),
            greaterThan: t('common:cloudinaryUploader.metadataPage.validationErrors.integer.greaterThan'),
            greaterThanEqual: t('common:cloudinaryUploader.metadataPage.validationErrors.integer.greaterThanEqual'),
          },
          number: {
            lessThan: t('common:cloudinaryUploader.metadataPage.validationErrors.number.lessThan'),
            lessThanEqual: t('common:cloudinaryUploader.metadataPage.validationErrors.number.lessThanEqual'),
            greaterThan: t('common:cloudinaryUploader.metadataPage.validationErrors.number.greaterThan'),
            greaterThanEqual: t('common:cloudinaryUploader.metadataPage.validationErrors.number.greaterThanEqual'),
          },
          enum: {
            oneOf: t('common:cloudinaryUploader.metadataPage.validationErrors.enum.oneOf'),
          },
          set: {
            oneOf: t('common:cloudinaryUploader.metadataPage.validationErrors.set.oneOf'),
          },
          date: {
            lessThan: t('common:cloudinaryUploader.metadataPage.validationErrors.date.lessThan'),
            lessThanEqual: t('common:cloudinaryUploader.metadataPage.validationErrors.date.lessThanEqual'),
            greaterThan: t('common:cloudinaryUploader.metadataPage.validationErrors.date.greaterThan'),
            greaterThanEqual: t('common:cloudinaryUploader.metadataPage.validationErrors.date.greaterThanEqual'),
          },
          stringError: t('common:cloudinaryUploader.metadataPage.validationErrors.stringError'),
          numberError: t('common:cloudinaryUploader.metadataPage.validationErrors.numberError'),
          integerError: t('common:cloudinaryUploader.metadataPage.validationErrors.integerError'),
          dateError: t('common:cloudinaryUploader.metadataPage.validationErrors.dateError'),
          enumError: t('common:cloudinaryUploader.metadataPage.validationErrors.enumError'),
          setError: t('common:cloudinaryUploader.metadataPage.validationErrors.setError'),
          required: t('common:cloudinaryUploader.metadataPage.validationErrors.required'),
          integerTypeError: t('common:cloudinaryUploader.metadataPage.validationErrors.integerTypeError'),
          digitsLimitError: t('common:cloudinaryUploader.metadataPage.validationErrors.digitsLimitError'),
          unsupportedFields: t('common:cloudinaryUploader.metadataPage.validationErrors.unsupportedFields'),
          unsupportedRequiredFields: t(
            'common:cloudinaryUploader.metadataPage.validationErrors.unsupportedRequiredFields'
          ),
        },
      },
    },
  };
};
